import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { AdapterDateFns } from "@mui/x-date-pickers-6/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers-pro-6";
import { DesktopDateRangePicker } from "@mui/x-date-pickers-pro-6/DesktopDateRangePicker";
import { endOfDay, parseISO, startOfDay } from "date-fns";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useDateFnsLocale } from "../../utils/date-locale";

interface DateRangePickerProps {
	value: [string, string];
	setValue: (value: [string, string]) => void;
	variant?: "outlined" | "standard" | "filled";
	size?: "small" | "medium";
	disableFuture?: boolean;
	// Advisory only — every date stays selectable. The cap is stated inside the
	// calendar so the limit is known before picking; a range that exceeds it is
	// still selectable and is reported back through `errorMessage`.
	maxRangeDays?: number;
	// Validation message for a range the caller rejected. Shown on the inputs
	// themselves and repeated inside the calendar, so it stays readable while
	// the calendar is open.
	errorMessage?: string;
}

const DateRangePicker = ({
	value,
	setValue,
	variant,
	size,
	disableFuture,
	maxRangeDays,
	errorMessage,
}: DateRangePickerProps) => {
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();

	// Rendered at the bottom of the calendar popover (the action bar slot). The
	// popover covers anything sitting under the inputs, so the limit — and the
	// error, when there is one — has to be repeated here to be readable while
	// the user is picking.
	const CalendarFooter = useCallback(({ className }: { className?: string }) => {
		if (!maxRangeDays && !errorMessage) {
			return null;
		}
		// className carries the layout's grid placement for the action bar row —
		// without it the footer lands in an arbitrary cell of the picker grid.
		return (
			<Box
				className={className}
				sx={{
					display: "flex",
					alignItems: "center",
					gap: "6px",
					padding: "8px 16px 12px 16px",
				}}
			>
				{errorMessage ? (
					<ErrorOutlineIcon sx={{ fontSize: "16px", color: "#D32F2F" }} />
				) : null}
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontSize: "12px",
						lineHeight: "16px",
						color: errorMessage ? "#D32F2F" : "rgba(0,0,0,0.6)",
					}}
				>
					{errorMessage ??
						t("common_date-range-limit", { count: maxRangeDays })}
				</Typography>
			</Box>
		);
	}, [errorMessage, maxRangeDays, t]);

	return (
		<Box>
			<LocalizationProvider
				dateAdapter={AdapterDateFns}
				adapterLocale={dateLocale}
			>
				<DesktopDateRangePicker
					localeText={{ start: t("common_start-date"), end: t("common_end-date") }}
					value={[parseISO(value[0]), parseISO(value[1])]}
					disableFuture={disableFuture}
					onChange={(newValue) => {
						if (newValue[0] === null || newValue[1] === null) {
							return;
						}
						setValue([
							startOfDay(newValue[0]).toISOString(),
							endOfDay(newValue[1]).toISOString(),
						]);
					}}
					slots={{ actionBar: CalendarFooter }}
					slotProps={{
						// A function so the message lands under the end input only —
						// helper text under both would read as two separate errors. It
						// wraps inside the input width, so it can never be clipped by
						// the right edge of the page.
						textField: (ownerState: { position?: "start" | "end" }) => ({
							variant: variant,
							size: size,
							error: Boolean(errorMessage),
							helperText:
								errorMessage && ownerState?.position === "end"
									? errorMessage
									: undefined,
							InputProps: {
								endAdornment: (
									<IconButton>
										<CalendarMonthIcon />
									</IconButton>
								),
							},
							FormHelperTextProps: {
								sx: { marginLeft: 0, whiteSpace: "normal" },
							},
						}),
					}}
				/>
			</LocalizationProvider>
		</Box>
	);
};

export default DateRangePicker;
